import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Phone, Mail, MapPin } from 'lucide-react';

// --- ANIMATION VARIANTS for Framer Motion ---
const sectionVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.6, -0.05, 0.01, 0.99],
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

// --- AnimatedSection Wrapper ---
const AnimatedSection = ({ children, id, className = '' }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, amount: 0.2 });

    return (
        <motion.section
            id={id}
            ref={ref}
            className={`py-24 md:py-32 overflow-hidden ${className}`}
            variants={sectionVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
        >
            {children}
        </motion.section>
    );
};

const AnimatedText = ({ text, className, variants, ...props }) => {
    const words = text.split(" ");
    return (
        <motion.p className={className} variants={variants} {...props}>
            {words.map((word, i) => (
                <motion.span key={i} className="inline-block mr-[0.25em]" variants={itemVariants}>
                    {word}
                </motion.span>
            ))}
        </motion.p>
    );
};

// --- CONTACT SECTION ---
const ContactSection = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [status, setStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const handleSubmit = async (e) => {
      e.preventDefault();
      setIsSubmitting(true);
      setStatus('Sending...');
      
      try {
          // --- THIS IS THE UPDATED PART ---
          // It now points to your live PHP script on Hostinger.
          const response = await fetch('https://smartsolutionslk.com/api/contact_handler.php', {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
              },
              body: JSON.stringify(formData),
          });

          const result = await response.json();

          if (response.ok) {
              setStatus(result.message);
              setFormData({ name: '', email: '', phone: '', message: '' }); // Clear form on success
          } else {
              // Throw an error to be caught by the catch block
              throw new Error(result.message || 'An error occurred.');
          }
      } catch (error) {
          console.error("Fetch Error:", error);
          setStatus(error.message || 'Failed to send message. Please try again.');
      } finally {
          setIsSubmitting(false);
      }
  };

  const contactInfo = [
    { 
      icon: Phone, 
      title: "Phone", 
      content: (
        <>
          <a href="tel:+94742111150" className="hover:text-cyan-400 transition-colors">+94 74 211 1150</a>
          <br />
          <a href="tel:+94701111150" className="hover:text-cyan-400 transition-colors">+94 70 111 1150</a>
        </>
      )
    },
    { icon: Mail, title: "Email", content: <a href="mailto:info@smartsolutionslk.com" className="hover:text-cyan-400 transition-colors">info@smartsolutionslk.com</a> },
    { icon: MapPin, title: "Address", content: "Ratnapura, Sri Lanka" },
  ];

  return (
    <AnimatedSection id="contact" className="bg-slate-100 dark:bg-gray-900 text-slate-900 dark:text-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
           <AnimatedText text="Let's Build Together." className="text-4xl md:text-6xl font-bold" variants={sectionVariants}/>
          <p className="text-lg text-slate-600 dark:text-gray-400 mt-4">Have an idea? We'd love to hear about it.</p>
        </div>
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
            <motion.div className="lg:w-1/2" variants={sectionVariants}>
              <h3 className="text-3xl font-bold mb-8">Get in Touch Directly</h3>
              <div className="space-y-8">
                  {contactInfo.map((item, index) => (
                      <motion.div key={index} className="flex items-start gap-4" variants={itemVariants}>
                         <div className="bg-white dark:bg-gray-800 p-3 rounded-full text-cyan-500 dark:text-cyan-400 shadow-md">
                           <item.icon size={24} />
                         </div>
                         <div>
                             <h4 className="text-xl font-semibold">{item.title}</h4>
                             <p className="text-slate-600 dark:text-gray-400">{item.content}</p>
                         </div>
                      </motion.div>
                  ))}
              </div>
            </motion.div>
            <motion.div className="lg:w-1/2" variants={sectionVariants}>
               <form onSubmit={handleSubmit} className="space-y-6 bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-lg">
                  <motion.div variants={itemVariants}>
                    <input type="text" name="name" placeholder="Your Name" value={formData.name} onChange={handleChange} required className="w-full p-4 bg-slate-50 dark:bg-gray-700 border border-slate-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400"/>
                  </motion.div>
                  <motion.div variants={itemVariants}>
                    <input type="email" name="email" placeholder="Your Email" value={formData.email} onChange={handleChange} required className="w-full p-4 bg-slate-50 dark:bg-gray-700 border border-slate-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400"/>
                  </motion.div>
                  <motion.div variants={itemVariants}>
                    <input type="tel" name="phone" placeholder="Your Phone Number" value={formData.phone} onChange={handleChange} required className="w-full p-4 bg-slate-50 dark:bg-gray-700 border border-slate-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400"/>
                  </motion.div>
                  <motion.div variants={itemVariants}>
                    <textarea name="message" placeholder="Your Message" rows="5" value={formData.message} onChange={handleChange} required className="w-full p-4 bg-slate-50 dark:bg-gray-700 border border-slate-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400"></textarea>
                  </motion.div>
                  <motion.div variants={itemVariants} className="text-center">
                    <button type="submit" disabled={isSubmitting} className="px-10 py-4 bg-cyan-500 text-black rounded-full font-semibold text-lg hover:bg-cyan-400 transition-all duration-300 transform hover:scale-105 w-full disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg">
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </button>
                  </motion.div>
                   {status && <p className="text-center mt-4 text-slate-500 dark:text-gray-300">{status}</p>}
               </form>
            </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

export default ContactSection;